import React from "react"
import { getCMSTemplateList } from "@/apis/apiCollection";
import { useTranslation } from "@/langs/useTranslation";
import DataTable from "@/components/DataTable";
import { ReactRender } from "@/components/ReactRender";
import * as dayjs from "dayjs";
import ScheduleTaskRange from "../Schedule/CreateTemplate/TaskCreator/TaskRange";
import ScheduleType from "../Schedule/CreateTemplate/TaskCreator/ScheduleType";
import { Button } from "@material-ui/core";
import { fullWindowTemplateId, orderTypeMap } from "./TemplateList/CONSTANT";
import HasPermission from "@/components/commons/HasPermission";

const CMSTemplateDatatable = React.forwardRef(({
  getSearchParams, footer, onView, onEdit, onApply, onPublish, onDelete, onLoaded, onPlay, onPause
}, ref) => {
  const { t } = useTranslation()

  const columns = [
    {
      data: "id",
      type: "checkbox"
    },
    {
      data: "name",
      title: t({ defaultMessage: "模板名称" }),
      searchable: true,
      orderable: true,
    },
    {
      data: "status",
      title: t({ defaultMessage: "模板状态" }),
      orderable: false,
      className: "center nowrap",
      createdCell: (td, data, row) => {
        ReactRender((
          <>
            {data === 1 && (
              <div style={{ padding: "2px 12px", textAlign: "center", borderRadius: 12, background: "rgba(82,88,221,0.1)", color: "#5258dd" }}>{t({ defaultMessage: "草稿" })}</div>
            )}
            {data === 10 && (
              <div style={{ padding: "2px 12px", textAlign: "center", borderRadius: 12, color: "#0fc6c2", background: "rgba(0,175,198,0.1)" }}>{t({ defaultMessage: "已发布" })}</div>
            )}
          </>
        ), td)
      }
    },
    {
      data: "contentTemplateTypeName",
      title: t({ defaultMessage: "类型" }),
      orderable: false
    },
    {
      data: "version",
      title: t({ defaultMessage: "发布版本" }),
      render: (data, _, row) => {
        return `${row.versionPrefix}${data}`
      },
      orderable: false
    },
    {
      data: "name",
      title: t({ defaultMessage: "播放时段" }),
      orderable: false,
      createdCell: (td, data, row) => {
        if (row.contentTemplateTypeId !== fullWindowTemplateId) {
          ReactRender( <div>-</div>, td)
          return
        }
        const detail = JSON.parse(row.contentTemplateDetail)
        const taskRangeData = {
          json: {
            start_datetime: Number(row.effectBeginTime),
            end_datetime: Number(row.effectEndTime),
            run_times: row.effectType===3? row.effectTimes: 0,
            end_type: row.effectType === 1 ? "forever" : row.effectType === 2 ? "datetime" : "times",
          }
        }
        const typeData = {
          schedule_type: detail.loopMode,
          json: {
            cycle_type: detail.loopMode === "weekly" ? "weekly_days" : detail.loopMode,
            cycle_value: detail.loopMode === "weekly" ? 1 : detail.loopDailyValue === "every_day" ? "every" : detail.loopDailyValue,
            cycle_days: detail.loopModeDays
          }
        }
        ReactRender(
          (
            <div style={{textWrap: "wrap", minWidth: 130}}>
              <div style={{width: "fit-content"}}>
                <ScheduleTaskRange data={taskRangeData} />
              </div>
              <div style={{width: "fit-content"}}>
                <ScheduleType data={typeData} />
              </div>
              <div style={{width: "fit-content"}}>
                {(detail.executeTimeRangeInfo || []).map(v => `${v.executeBeginTime.hour.toString().padStart(2, '0')}:${v.executeBeginTime.minute.toString().padStart(2, '0')} - ${v.executeEndTime.hour.toString().padStart(2, '0')}:${v.executeEndTime.minute.toString().padStart(2, '0')}`).join(", ")}
              </div>
            </div>
          )
          , td
        );
      },
    },
    {
      data: "name",
      title: t({ defaultMessage: "播放状态" }),
      orderable: false,
      className: "center nowrap",
      createdCell: (td, data, row) => {
        if (row.contentTemplateTypeId !== fullWindowTemplateId) {
          ReactRender( <div>-</div>, td)
          return
        }
        const display = JSON.parse(row.contentTemplateDetail).display
        ReactRender(
          <>
            <HasPermission permission="content_template_list_play_template">
              <Button size="small" color="primary" onClick={() => onPlay(row)} disabled={display === 1}>
                {t({ defaultMessage: "播放" })}
              </Button>
            </HasPermission>
            <HasPermission permission="content_template_list_pause_template">
              <Button size="small" color="primary" onClick={() => onPause(row)} disabled={display === 2}>
                {t({ defaultMessage: "暂停" })}
              </Button>
            </HasPermission>
          </>
          , td
        )
      }
    },
    {
      data: "createUserName",
      title: t({ defaultMessage: "创建人" }),
      orderable: true,
    },
    {
      data: "createdAt",
      title: t({ defaultMessage: "创建时间" }),
      orderable: true,
      render: data => {
        return dayjs(Number(data)).format("YYYY/MM/DD HH:mm");
      }
    },
    {
      data: "updatedAt",
      title: t({ defaultMessage: "最后修改时间" }),
      orderable: true,
      render: data => {
        return dayjs(Number(data)).format("YYYY/MM/DD HH:mm")
      }
    },
    {
      data: "publishedAt",
      title: t({ defaultMessage: "最后发布时间" }),
      render: data => {
        if (Number(data) === 0 || !data) return "-"
        return dayjs(Number(data)).format("YYYY/MM/DD HH:mm")
      },
      orderable: false
    },
    {
      data: "name",
      title: t({ defaultMessage: "操作" }),
      className: "center nowrap",
      createdCell: (td, data, row) => {
        ReactRender(
          <>
            <HasPermission permission="content_template_list_edit_template">
              <Button size="small" color="primary" onClick={() => onEdit(row)}>
                {t({ defaultMessage: "编辑" })}
              </Button>
            </HasPermission>
            <HasPermission permission="content_template_list_publish_template">
              <Button size="small" color="primary" onClick={() => onPublish(row)} disabled={row.status === 10}>
                {t({ defaultMessage: "发布" })}
              </Button>
            </HasPermission>
            <HasPermission permission="content_template_list_apply_template">
              <Button size="small" color="primary" onClick={() => onApply(row)}>
                {t({ defaultMessage: "应用" })}
              </Button>
            </HasPermission>
            <HasPermission permission="content_template_list_delete_template">
              <Button size="small" color="secondary" onClick={() => onDelete(row)}>
                {t({ defaultMessage: "删除" })}
              </Button>
            </HasPermission>
          </>
          , td
        )
      },
      orderable: false
    },
  ]

  const ajax = (data, callback) => {
    let orderType;
    let orderWay;
    const order = data.order[0];
    if (order && data.columns[order.column]?.orderable) {
      orderType = orderTypeMap[data.columns[order.column]?.name];
      // orderType = order.column;
      orderWay = order.dir === "asc" ? 1 : 2;
    }
    return getCMSTemplateList({
      orderType,
      orderWay,
      ...data,
      ...getSearchParams()
    }).then(res => {
      callback({
        data: res.contentTemplates,
        // draw: data.draw,
        recordsFiltered: res.total,
        recordsTotal: res.total
      });
    });
  };

  return (
    <DataTable
      ref={ref}
      ordering
      columns={columns}
      ajax={ajax}
      // className={className}
      // style={style}
      footer={footer}
      scrollable
      showCheckAll={false}
      // onCheckAll={onCheckAll}
      onLoaded={onLoaded}
      onRowClick={onView}
    />
  )

})

export default CMSTemplateDatatable